import { JobStatus, Job } from '@applicator/shared'

import { supabase } from '../../lib/supabase'
import { TableColumn } from '../Table'

import { renderPositionColumn, renderCompanyColumn, renderStatusColumn, renderScoreColumn, renderVerdictColumn } from './columns/renderers'
import { CreateColumnsParams } from './columns/types'
import { formatSalaryForDisplay, getStatusDisplay, unformatNumber } from './utils'

export type { CreateColumnsParams }

export const createTableColumns = ({
  jobs,
  supabaseJobs,
  analyzingJobIds,
  statusOptions,
  searchQueryId,
  setJobs,
  setError,
  handleStatusChange,
  handleDateChange,
  handleQuickAction,
  handleExcitementChange,
  handleAnalyzeRelevance,
  handleDeleteJob,
  handleAddToJobs,
  handleRejectRelevant,
}: CreateColumnsParams): TableColumn<Job>[] => {
  const columns: TableColumn<Job>[] = [
    {
      key: 'jobNo',
      label: '#',
      sortable: false,
      required: true,
      minWidth: 50,
      align: 'center',
      render: (_value, job) => <span className="text-sm text-text-primary">{job.jobNo || '-'}</span>,
    },
    {
      key: 'position',
      label: 'Job Position',
      sortable: true,
      showTooltip: true,
      render: (_value, job, index) => renderPositionColumn(job, index, jobs, supabaseJobs),
    },
    {
      key: 'company',
      label: 'Company',
      sortable: true,
      showTooltip: true,
      render: (_value, job, index) => renderCompanyColumn(job, index, jobs, supabaseJobs),
    },
    {
      key: 'salary',
      label: 'Salary',
      sortable: true,
      editable: {
        enabled: true,
        onSave: async (row, value) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          
          const numericValue = unformatNumber(value)
          const salaryMax = numericValue ? parseInt(numericValue, 10) : 0
          const formattedSalary = numericValue ? `$${numericValue}` : '$0'

          // Optimistic update
          const updatedJobs = [...jobs]
          updatedJobs[rowIndex] = { ...updatedJobs[rowIndex], salary: formattedSalary }
          setJobs(updatedJobs)

          // Update in Supabase
          if (row.id) {
            const { error } = await supabase
              .from('jobs')
              .update({ 
                salary_max: salaryMax,
                salary_currency: 'USD'
              })
              .eq('id', row.id)

            if (error) {
              // Revert on error
              setJobs(jobs)
              setError(error.message)
            }
          }
        },
        placeholder: 'Enter amount',
      },
      render: (_value, job) => {
        return (
          <span className="text-sm text-text-primary hover:text-interactive transition-colors">
            {formatSalaryForDisplay(job.salary)}
          </span>
        )
      },
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      showTooltip: true,
      editable: {
        enabled: true,
        onSave: async (row, value) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          
          const locationValue = value.trim() || 'N/A'

          // Optimistic update
          const updatedJobs = [...jobs]
          updatedJobs[rowIndex] = { ...updatedJobs[rowIndex], location: locationValue }
          setJobs(updatedJobs)

          // Update in Supabase
          if (row.id) {
            const { error } = await supabase
              .from('jobs')
              .update({ location: locationValue === 'N/A' ? null : locationValue })
              .eq('id', row.id)

            if (error) {
              // Revert on error
              setJobs(jobs)
              setError(error.message)
            }
          }
        },
        placeholder: 'Enter location',
      },
      render: (_value, job) => {
        return <span className="block truncate text-sm text-text-primary">{job.location}</span>
      },
    },
    // Status column - only show when NOT viewing search query results
    ...(searchQueryId ? [] : [{
      key: 'status',
      label: 'Status',
      sortable: true,
      dropdown: {
        enabled: true,
        options: statusOptions.map(status => ({
          value: status,
          label: getStatusDisplay(status),
        })),
        onSelect: async (row, value) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          await handleStatusChange(rowIndex, value as JobStatus)
        },
        getCurrentValue: (row) => row.status,
      },
      render: (_value, job) => renderStatusColumn(job),
    }]),
    {
      key: 'dateSaved',
      label: 'Date Saved',
      sortable: true,
      minWidth: 160,
      render: (_value, job) => {
        return <span className="text-sm text-text-secondary">{job.dateSaved}</span>
      },
    },
    {
      key: 'dateApplied',
      label: 'Date Applied',
      sortable: true,
      minWidth: 160,
      datePicker: {
        enabled: true,
        includeTime: true,
        onDateChange: async (row, date) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          await handleDateChange(rowIndex, 'dateApplied', date || '')
        },
        onQuickAction: async (row, action) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          await handleQuickAction(rowIndex, 'dateApplied', action)
        },
        getCurrentValue: (row) => row.dateApplied,
        showRevert: true,
      },
      render: (_value, job) => {
        return <span className="text-sm text-text-primary">{job.dateApplied}</span>
      },
    },
    {
      key: 'followUp',
      label: 'Follow up',
      sortable: true,
      minWidth: 120,
      datePicker: {
        enabled: true,
        includeTime: false,
        onDateChange: async (row, date) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          await handleDateChange(rowIndex, 'followUp', date || '')
        },
        onQuickAction: async (row, action) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          await handleQuickAction(rowIndex, 'followUp', action)
        },
        getCurrentValue: (row) => row.followUp,
        showRevert: true,
      },
      render: (_value, job) => {
        return <span className="text-sm text-text-secondary">{job.followUp}</span>
      },
    },
    {
      key: 'deadline',
      label: 'Deadline',
      sortable: true,
      minWidth: 160,
      datePicker: {
        enabled: true,
        includeTime: true,
        onDateChange: async (row, date) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          await handleDateChange(rowIndex, 'deadline', date || '')
        },
        onQuickAction: async (row, action) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          await handleQuickAction(rowIndex, 'deadline', action)
        },
        getCurrentValue: (row) => row.deadline,
        showRevert: true,
      },
      render: (_value, job) => {
        return <span className="text-sm text-text-primary">{job.deadline}</span>
      },
    },
    {
      key: 'excitement',
      label: 'Excitement',
      sortable: true,
      minWidth: 100,
      rating: {
        enabled: true,
        max: 5,
        onChange: async (row, value) => {
          const rowIndex = jobs.findIndex(j => j.id === row.id)
          if (rowIndex < 0) return
          await handleExcitementChange(rowIndex, value)
        },
        getCurrentValue: (row) => row.excitement || 0,
      },
    },
    {
      key: 'score',
      label: 'Score',
      sortable: true,
      align: 'center',
      render: (_value, job, index) => renderScoreColumn(job, index, jobs, supabaseJobs),
    },
    {
      key: 'verdict',
      label: 'Verdict',
      sortable: true,
      align: 'center',
      render: (_value, job, index) => renderVerdictColumn(job, index, jobs, supabaseJobs),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      sticky: 'right',
      width: '48px',
      minWidth: '48px',
      render: (_value, job, index) => {
        const originalIndex = job.id ? jobs.findIndex((j) => j.id === job.id) : -1
        const safeOriginalIndex = originalIndex >= 0 ? originalIndex : index
        const jobId = job.id || safeOriginalIndex.toString()
        const supabaseJob = supabaseJobs.find(j => j.id === jobId)
        const isRelevant = supabaseJob?.relevance_verdict === 'Fit'
        const isRejected = supabaseJob?.rejected_from_relevant === true
        
        // Determine which buttons to show
        const showAnalyzeButton = (supabaseJob?.is_relevant === null || supabaseJob?.is_relevant === undefined)
        const showAddToJobsButton = searchQueryId && handleAddToJobs && isRelevant && !isRejected
        const showRejectButton = searchQueryId && handleRejectRelevant && isRelevant && !isRejected
        // const showDeleteButton = true // Delete is always available (currently unused)
        
        // Count available buttons (currently unused, but may be needed for future UI improvements)
        // const buttonCount = (showAnalyzeButton ? 1 : 0) + (showAddToJobsButton ? 1 : 0) + (showRejectButton ? 1 : 0) + (showDeleteButton ? 1 : 0)
        // Determine which single button to show (if only one)
        // const _isSingleButton = buttonCount === 1
        // const _singleButton = showDeleteButton ? 'delete' :
        //                     showAnalyzeButton ? 'analyze' :
        //                     showAddToJobsButton ? 'addToJobs' :
        //                     showRejectButton ? 'reject' : null
        
        // Unified button style - all buttons use outlined style with consistent padding and sizing
        const buttonBaseStyle = "w-8 h-8 flex items-center justify-center rounded border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
        
        // Check if this is a relevant vacancy (has Start Applying or Reject buttons)
        const isRelevantVacancy = showAddToJobsButton || showRejectButton
        
        return (
          <div className="relative flex items-center justify-center w-full h-full group">
            {/* Default visible button */}
            {isRelevantVacancy ? (
              // For relevant vacancies: show "Start Applying" button by default, or three dots if both buttons available
              <div className="relative w-8 h-8 flex items-center justify-center">
                {/* Three dots - visible when both Start Applying and Reject are available */}
                {showAddToJobsButton && showRejectButton && (
                  <button
                    className="w-full h-full flex items-center justify-center rounded border border-border-strong text-text-muted hover:text-text-secondary hover:border-border-default hover:bg-surface-subtle transition-colors"
                    title="Actions available"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                )}
                
                {/* Start Applying button - visible by default if only this button is available */}
                {showAddToJobsButton && !showRejectButton && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation()
                      if (handleAddToJobs) {
                        await handleAddToJobs(safeOriginalIndex)
                      }
                    }}
                    className={`${buttonBaseStyle} border-brand-primary/30 text-brand-primary hover:border-brand-primary hover:bg-brand-primary-subtle focus:ring-interactive-focus-ring`}
                    title="Start applying to this job"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
                
                {/* Delete button - appears on hover, replacing three dots or Start Applying */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteJob(safeOriginalIndex)
                    }}
                    className={`${buttonBaseStyle} border-danger/30 text-danger hover:border-danger hover:bg-danger-subtle focus:ring-danger`}
                    title="Delete job"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              // For non-relevant vacancies: Delete button always visible
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteJob(safeOriginalIndex)
                }}
                className={`${buttonBaseStyle} border-border-strong text-text-secondary hover:text-danger hover:border-danger/30 hover:bg-danger-subtle focus:ring-danger`}
                title="Delete job"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
            
            {/* Additional action buttons that appear on hover - positioned absolutely to the left */}
            {isRelevantVacancy && (
              <>
                {/* Gradient background that spans full row height */}
                <div 
                  className="absolute right-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto z-40"
                  style={{
                    top: '-12px',
                    bottom: '-12px',
                    width: '180px',
                    background: 'linear-gradient(to left, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 50px, rgba(255, 255, 255, 0.95) 80px, rgba(255, 255, 255, 0.85) 120px, rgba(255, 255, 255, 0.65) 150px, rgba(255, 255, 255, 0.3) 170px, rgba(255, 255, 255, 0) 180px)',
                  }}
                />
                
                {/* Buttons container */}
                <div 
                  className="absolute right-full flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out pointer-events-none group-hover:pointer-events-auto mr-1 z-50"
                >
                  {/* Analyze button */}
                  {showAnalyzeButton && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (supabaseJob && job.id) {
                          handleAnalyzeRelevance(
                            job.id,
                            job.position,
                            job.company,
                            job.location
                          )
                        }
                      }}
                      disabled={analyzingJobIds.has(jobId)}
                      className={`${buttonBaseStyle} border-brand-primary/30 text-brand-primary hover:border-brand-primary hover:bg-brand-primary-subtle focus:ring-interactive-focus-ring transform translate-x-2 group-hover:translate-x-0 shadow-sm bg-surface-default ${
                        analyzingJobIds.has(jobId)
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      title="Analyze relevance"
                    >
                      {analyzingJobIds.has(jobId) ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )}
                    </button>
                  )}
                  
                  {/* Start Applying button - shown on hover if both Start Applying and Reject are available */}
                  {showAddToJobsButton && showRejectButton && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        if (handleAddToJobs) {
                          await handleAddToJobs(safeOriginalIndex)
                        }
                      }}
                      className={`${buttonBaseStyle} border-brand-primary/30 text-brand-primary hover:border-brand-primary hover:bg-brand-primary-subtle focus:ring-interactive-focus-ring transform translate-x-2 group-hover:translate-x-0 shadow-sm bg-surface-default`}
                      title="Start applying to this job"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                  
                  {/* Reject button */}
                  {showRejectButton && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        const reason = window.prompt('Why are you rejecting this relevant job?')
                        if (!reason || !reason.trim()) {
                          return
                        }
                        if (handleRejectRelevant) {
                          await handleRejectRelevant(safeOriginalIndex, reason)
                        }
                      }}
                      className={`${buttonBaseStyle} border-danger/30 text-danger hover:border-danger hover:bg-danger-subtle focus:ring-danger transform translate-x-2 group-hover:translate-x-0 shadow-sm bg-surface-default`}
                      title="Reject this relevant job"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )
      },
    },
  ]
  
  return columns
}
